import { VoiceBackfill } from './VoiceBackfill';
import { ReportWorkspace } from './ReportWorkspace';
import type { OfflineEpisodeContext } from './offlineContext';
import { useQuery } from '@tanstack/react-query';
import { Phase3Workspace } from './Phase3Workspace';
import { PostpartumCourseTasks } from './PostpartumCourseTasks';
import { fetchPostpartumCourse, scheduleCourseTask } from './postpartumCourseApi';
import { encounterAllowsSubject } from './courseEncounterScope';
import { clientApi } from '@/config/axios';
import { pendingBirthEntries } from './pendingBirthEntries';
import { DyadReview } from './DyadReview';
import { BirthWorkspace } from './BirthWorkspace';
import { withNewbornSubjects } from './newbornSubjects';
import { useDatingRefresh } from './useDatingRefresh';
import { EpisodeAdministration } from './EpisodeAdministration';
import { withCustomColumns } from './customDefinitions';
import { CustomObservationEntry } from './CustomObservationEntry';
import { ConsentArtifacts } from './ConsentArtifacts';
import { PrenatalContextEditor } from './PrenatalContextEditor';
import { DatingReview } from './DatingReview';
import { EpisodeTimeline } from './EpisodeTimeline';
import { CareStatusEntry } from './CareStatusEntry';
import { evaluatePrenatalRules } from './prenatalRules';
import { evaluateRules } from './rules';
import { formatInstant } from '@/lib/utils/date';
import { usePatient } from '@/features/emr/api/patients-api';
import { useState } from 'react';
import { useOrgTimezone } from '@/lib/timezone';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { usePregnancies } from '@/features/emr/api/pregnancies-api';
import { useEncounterTimeline } from '@/features/emr/api/encounters-api';
import type { PregnancyOut } from '@/features/emr/types';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { useClinicalEpisodes, useClinicalEvents, useStartEpisode, usePracticeConfiguration } from './api';
import { prenatalDefinition } from './definitions';
import { PrenatalEntry, type ProposedEntry } from './PrenatalEntry';
import { PrenatalGrid } from './PrenatalGrid';
import { VisitSignOff } from './VisitSignOff';
import { PrenatalHeader } from './PrenatalHeader';
import { FetalHeartTrends } from './FetalHeartTrends';
import { PrenatalTrends } from './PrenatalTrends';
import { PracticeChecklist } from './PracticeChecklist';
import { OfflineControls } from './OfflineControls';
import { useOfflineFlowsheet } from './useOfflineFlowsheet';
import { EventHistory, replayIdentity } from './EventHistory';
import type { ClinicalEpisode, ClinicalEvent, EventInput } from './types';

export function MidwiferyFlowsheet({ patientId }: { patientId: string }) {
  const { permissions } = usePermissions();
  const { data, isLoading, isError } = usePregnancies(patientId);
  const [chosen, setChosen] = useState('');
  if (!permissions.includes('read_flowsheet'))
    return (
      <Alert>
        <AlertDescription>
          Midwifery flowsheets require clinical flowsheet access from your organization.
        </AlertDescription>
      </Alert>
    );
  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (isError)
    return (
      <Alert variant="destructive">
        <AlertDescription>Pregnancies could not be loaded. Please try again.</AlertDescription>
      </Alert>
    );
  const pregnancies = data?.data ?? [];
  const pregnancy =
    pregnancies.find((p) => p.id === chosen) ?? pregnancies.find((p) => p.status === 'active') ?? pregnancies[0];
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Midwifery prenatal flowsheet</CardTitle>
          <CardDescription>
            Observations are stored with their clinical time, subject, and author. Unreviewed proposals do not appear in
            the confirmed grid or trends.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pregnancy ? (
            <FieldGroup>
              <Field>
                <FieldLabel>Pregnancy</FieldLabel>
                <Select value={pregnancy.id} onValueChange={setChosen}>
                  <SelectTrigger className="min-h-11" aria-label="Pregnancy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pregnancies.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.edd ? `EDD ${p.edd}` : 'EDD not set'} · {p.status}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            </FieldGroup>
          ) : (
            <p>
              Create a pregnancy in Details before starting the midwifery flowsheet. Then a team member with create
              clinical event permission can start the episode.
            </p>
          )}
        </CardContent>
      </Card>
      {pregnancy && <PregnancyFlowsheet key={pregnancy.id} pregnancy={pregnancy} patientId={patientId} />}
    </div>
  );
}
function PregnancyFlowsheet({ pregnancy, patientId }: { pregnancy: PregnancyOut; patientId: string }) {
  const { organization, user } = useAuth();
  const episodes = useClinicalEpisodes(pregnancy.id);
  const start = useStartEpisode(pregnancy.id);
  const { permissions } = usePermissions();
  if (episodes.isLoading) return <Skeleton className="h-24 w-full" />;
  if (episodes.isError)
    return (
      <Alert variant="destructive">
        <AlertDescription>
          The flowsheet could not be loaded.{' '}
          <Button variant="outline" onClick={() => void episodes.refetch()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  const episode = episodes.data?.[0];
  if (!episode)
    return (
      <Card>
        <CardHeader>
          <CardTitle>Start prenatal documentation</CardTitle>
          <CardDescription>This creates a separate clinical event record for this pregnancy.</CardDescription>
        </CardHeader>
        <CardContent>
          {(permissions.includes('create_clinical_event') ||
            permissions.includes('create_supervised_clinical_event')) && (
            <Button disabled={start.isPending} onClick={() => start.mutate()}>
              Start midwifery flowsheet
            </Button>
          )}
          {!permissions.includes('create_clinical_event') &&
            !permissions.includes('create_supervised_clinical_event') && (
              <p>
                You have read-only flowsheet access. A team member with create clinical event permission must start this
                pregnancy’s episode before its flowsheets are available.
              </p>
            )}
          {start.isError && <p role="alert">Could not start the flowsheet.</p>}
        </CardContent>
      </Card>
    );
  return (
    <EpisodeView
      key={`${organization?.organization_id}:${user?.id}:${episode.id}`}
      episode={episode}
      pregnancy={pregnancy}
      patientId={patientId}
    />
  );
}
function EpisodeView({
  episode,
  pregnancy,
  patientId,
  scopedSubjectId,
  initialEncounterId,
}: {
  scopedSubjectId?: string;
  initialEncounterId?: string;
  episode: ClinicalEpisode;
  pregnancy: PregnancyOut;
  patientId: string;
}) {
  const { timeZone } = useOrgTimezone();
  const events = useClinicalEvents(episode.id);
  const configuration = usePracticeConfiguration();
  const configuredDefinition = withCustomColumns(
    prenatalDefinition,
    configuration.data?.custom_observations ?? [],
    configuration.data?.column_labels ?? {},
  );
  const columns = configuredDefinition.columns.filter((c) => !configuration.data?.hidden_columns.includes(c.key));
  const order = configuration.data?.column_order ?? [];
  columns.sort((a, b) => {
    const ai = order.indexOf(a.key),
      bi = order.indexOf(b.key);
    return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
  });
  const definition = { ...configuredDefinition, columns };
  const encounters = useEncounterTimeline(patientId);
  const { permissions } = usePermissions();
  const { organization, user } = useAuth();
  const course = useQuery({
    queryKey: ['postpartum-course', organization?.organization_id, episode.id],
    enabled: !!organization && permissions.includes('read_flowsheet'),
    queryFn: () => fetchPostpartumCourse(organization!.organization_id, episode.id),
  });
  const options = [
    ...new Map(
      [
        ...(encounters.data?.pages.flatMap((page) => page.data) ?? []).filter((e) => e.pregnancy_id === pregnancy.id),
        ...(course.data?.encounters ?? []).map((e) => ({ ...e, encounter_date: e.date, encounter_type: e.label })),
      ].map((e) => [e.id, e]),
    ).values(),
  ].filter(
    (e) =>
      !scopedSubjectId ||
      ('allowed_subject_ids' in e &&
        e.allowed_subject_ids?.length === 1 &&
        e.allowed_subject_ids.includes(scopedSubjectId)),
  );
  const offline = useOfflineFlowsheet({
    organizationId: organization?.organization_id ?? '',
    userId: user?.id ?? '',
    episode,
    onlineEvents: events.data,
    context: { pregnancy, encounters: options, timeZone, postpartum_course_tasks: course.data?.tasks },
  });
  const [encounterId, setEncounterId] = useState(initialEncounterId ?? '');
  const [formLocked, setFormLocked] = useState(false);
  const [clearRequested, setClearRequested] = useState(false);
  const [formEpoch, setFormEpoch] = useState(0);
  const [editRequest, setEditRequest] = useState<ClinicalEvent>();
  const patient = usePatient(permissions.includes('read_patient') ? patientId : undefined);
  async function save(entries: ProposedEntry[], occurredAt: string) {
    const allowedEpisode = await withNewbornSubjects(episode);
    if (
      !organization ||
      !encounterId ||
      !offline.unlocked ||
      !offline.cacheReady ||
      episode.administration?.closed ||
      !options.some((e) => e.id === encounterId) ||
      entries.some(
        (entry) =>
          !allowedEpisode.subjects.some((s) => s.id === entry.subject_id && s.active !== false) ||
          !encounterAllowsSubject(
            options.find((e) => e.id === encounterId),
            episode,
            entry.subject_id,
          ),
      )
    )
      throw new Error('Unlock local storage and select an encounter');
    const inputs: EventInput[] = entries.map((entry) => ({
      ...entry,
      ...replayIdentity(),
      encounter_id: encounterId,
      schema_version: entry.schema_version ?? 1,
      occurred_at: occurredAt,
      recorded_at: new Date().toISOString(),
      source: 'MANUAL',
    }));
    await offline.save(inputs);
  }
  const list = offline.unlocked ? offline.cachedEvents : (events.data ?? []);
  useDatingRefresh(organization?.organization_id ?? '', episode.id, list);
  const baseline = pregnancy.pre_pregnancy_weight ? pregnancy.pre_pregnancy_weight * 453.59237 : null;
  const heightCm = pregnancy.height_inches ? pregnancy.height_inches * 2.54 : null;
  const flags = configuration.data
    ? [
        ...evaluateRules(list, configuration.data),
        ...evaluatePrenatalRules(list, configuration.data, {
          edd: pregnancy.edd,
          onDate: formatInstant(new Date().toISOString(), 'yyyy-MM-dd', timeZone),
          timeZone,
          prePregnancyWeightGrams: baseline,
          heightCm,
          fetusCount: pregnancy.fetus_count,
          active: pregnancy.status === 'active' && !episode.administration?.closed,
        }),
      ]
    : [];
  const motherId = episode.subjects.find((subject) => subject.subject_type === 'MOTHER')?.id ?? '';
  const exportContext = `${[patient.data?.metadata_?.first_name, patient.data?.metadata_?.last_name].filter(Boolean).join(' ') || 'Patient'} · DOB ${patient.data?.metadata_?.date_of_birth ?? 'not available'} · Patient ID ${patientId} · Pregnancy ${pregnancy.id} · EDD ${pregnancy.edd ?? 'not set'}`;
  const entryDisabled = !encounterId || !offline.unlocked || !offline.cacheReady || !!episode.administration?.closed;
  const activeSubjects = episode.subjects.filter(
    (subject) =>
      subject.active !== false &&
      encounterAllowsSubject(
        options.find((e) => e.id === encounterId),
        episode,
        subject.id,
      ),
  );
  return (
    <div
      key={formEpoch}
      className="flex flex-col gap-6"
      onInputCapture={(e) => {
        if ((e.target as Element).closest('form,[data-encounter-draft]')) setFormLocked(true);
      }}
      onChangeCapture={(e) => {
        if ((e.target as Element).closest('form,[data-encounter-draft]')) setFormLocked(true);
      }}
      onClickCapture={(e) => {
        const target = e.target as Element;
        if (target.closest('button') && target.closest('form,[data-encounter-draft]')) setFormLocked(true);
      }}>
      <PrenatalHeader
        pregnancy={pregnancy}
        events={list}
        subjects={episode.subjects}
        timeZone={timeZone}
        configuration={configuration.data}
      />
      {!permissions.includes('create_clinical_event') && !permissions.includes('create_supervised_clinical_event') && (
        <Field>
          <FieldLabel>Encounter for reports and views</FieldLabel>
          <Select value={encounterId} onValueChange={setEncounterId} disabled={formLocked}>
            <SelectTrigger aria-label="Encounter for reports and views">
              <SelectValue placeholder="Choose an encounter" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {options.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.encounter_date} · {e.encounter_type || 'Visit'}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      )}
      <OfflineControls controller={offline} subjects={episode.subjects} />
      {events.isLoading && !offline.cacheReady && <Skeleton className="h-20 w-full" />}
      {events.isError && (
        <Alert>
          <AlertDescription>
            Live observations could not be loaded. Unlock the saved workspace to view its cached observations.{' '}
            <Button variant="outline" onClick={() => void events.refetch()}>
              Retry connection
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Prenatal observations</CardTitle>
          <CardDescription>
            {pregnancy.edd
              ? `EDD ${pregnancy.edd} · ${pregnancy.edd_method ?? 'Dating method not recorded'}`
              : 'EDD not set — gestational age is unavailable'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PrenatalGrid
            events={list}
            subjects={episode.subjects}
            edd={pregnancy.edd}
            timeZone={timeZone}
            definition={definition}
            exportContext={exportContext}
            prePregnancyWeightGrams={baseline}
            flags={flags}
            onEdit={(event) => setEditRequest(event)}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Trends</CardTitle>
          <CardDescription>
            Confirmed observations only. Reference bands appear only when enabled in reviewed practice configuration.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PrenatalTrends
            events={list}
            timeZone={timeZone}
            edd={pregnancy.edd}
            configuration={configuration.data}
            prePregnancyWeightGrams={baseline}
            heightCm={heightCm}
            fetusCount={pregnancy.fetus_count}
          />
          <FetalHeartTrends events={list} subjects={episode.subjects} timeZone={timeZone} edd={pregnancy.edd} />
        </CardContent>
      </Card>
      {(permissions.includes('create_clinical_event') || permissions.includes('create_supervised_clinical_event')) && (
        <Card>
          <CardHeader>
            <CardTitle>Record prenatal observations</CardTitle>
            <CardDescription>
              Leave unmeasured fields blank. Complete both blood pressure values and temperature method when recording
              them.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <FieldGroup>
              <Field>
                <FieldLabel>Encounter</FieldLabel>
                <Select value={encounterId} onValueChange={setEncounterId} disabled={formLocked}>
                  <SelectTrigger className="min-h-11" aria-label="Encounter">
                    <SelectValue placeholder="Choose the pregnancy encounter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {options.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.encounter_date} · {e.encounter_type ?? 'Encounter'}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {formLocked && (
                  <div role="status" className="space-y-2">
                    <p>
                      Form values are bound to the selected encounter. Save any needed entries before clearing the forms
                      to change encounters. Saved and pending observations are retained.
                    </p>
                    <Button type="button" variant="outline" onClick={() => setClearRequested(true)}>
                      Clear forms to change encounter
                    </Button>
                    {clearRequested && (
                      <div role="alert">
                        <p>
                          Discard all current unsaved form values? Saved observations and the pending queue will remain.
                        </p>
                        <Button
                          type="button"
                          onClick={() => {
                            setFormEpoch((v) => v + 1);
                            setFormLocked(false);
                            setClearRequested(false);
                          }}>
                          Discard form values
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setClearRequested(false)}>
                          Keep editing
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {encounters.isLoading && <p>Loading linked encounters…</p>}
                {encounters.isError && (
                  <p role="alert">
                    Encounter choices could not be fully loaded.{' '}
                    <Button variant="outline" onClick={() => void encounters.refetch()}>
                      Retry encounters
                    </Button>
                  </p>
                )}
                {encounters.hasNextPage && (
                  <Button
                    variant="outline"
                    disabled={encounters.isFetchingNextPage}
                    onClick={() => void encounters.fetchNextPage()}>
                    {encounters.isFetchingNextPage ? 'Loading older encounters…' : 'Load older encounters'}
                  </Button>
                )}
                {!options.length && !encounters.isLoading && !encounters.isError && (
                  <p>
                    No linked encounters in the loaded history. Load older encounters or create an encounter linked to
                    this pregnancy.
                  </p>
                )}
              </Field>
            </FieldGroup>
            <CustomObservationEntry
              definitions={
                configuration.data?.clinically_reviewed ? (configuration.data.custom_observations ?? []) : []
              }
              subjects={activeSubjects}
              onSave={save}
              disabled={entryDisabled}
            />
            <PrenatalEntry
              events={list}
              configuration={configuration.data}
              subjects={activeSubjects}
              onSave={save}
              disabled={entryDisabled}
            />
          </CardContent>
        </Card>
      )}
      {!scopedSubjectId && (
        <div data-encounter-draft key={`birth-${formEpoch}`}>
          <BirthWorkspace
            lookupMedicationLots={async (drug) =>
              (
                await clientApi.get(
                  `/api/v1/organizations/${organization?.organization_id}/flowsheets/medication-lots`,
                  {
                    params: { drug },
                  },
                )
              ).data
            }
            edd={pregnancy.edd}
            timerActions={offline.timerActions}
            onTimerAction={offline.saveTimerAction}
            pendingEntries={pendingBirthEntries(offline.pending, episode.id)}
            episode={episode}
            events={list}
            encounterId={encounterId}
            timeZone={timeZone}
            configuration={configuration.data}
            disabled={entryDisabled}
            onSave={save}
            onEdit={setEditRequest}
          />
        </div>
      )}
      {encounterId && organization && (
        <VoiceBackfill
          organizationId={organization.organization_id}
          episode={episode}
          encounterId={encounterId}
          allowedSubjectIds={activeSubjects.map((s) => s.id)}
          templates={configuration.data?.clinically_reviewed ? configuration.data.note_templates : []}
          onSaved={() => void events.refetch()}
          offline={!!offline.offlineSince}
        />
      )}
      <ReportWorkspace
        key={`${organization?.organization_id}:${episode.id}:${encounterId}`}
        organizationId={organization?.organization_id ?? ''}
        episode={episode}
        events={list}
        encounterId={encounterId}
        timeZone={timeZone}
        configuration={configuration.data}
      />
      <Phase3Workspace
        key={`course-${formEpoch}`}
        episode={episode}
        events={list}
        encounterId={encounterId}
        timeZone={timeZone}
        configuration={configuration.data}
        disabled={entryDisabled}
        onSave={save}
        onEdit={setEditRequest}
        encounters={options}
      />
      {course.isError && (
        <Alert>
          <AlertDescription>
            Follow-up tasks could not be loaded.{' '}
            <Button variant="outline" onClick={() => course.refetch()}>
              Retry follow-up tasks
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <PostpartumCourseTasks
        tasks={course.data?.tasks ?? []}
        subjects={episode.subjects}
        timeZone={timeZone}
        disabled={entryDisabled}
        onSave={save}
        onReviewSource={(id) => setEditRequest(list.find((e) => e.id === id))}
        onSchedule={
          permissions.includes('create_encounter')
            ? async (input) => {
                const result = await scheduleCourseTask(organization!.organization_id, episode.id, input);
                await Promise.all([course.refetch(), encounters.refetch()]);
                return result;
              }
            : undefined
        }
      />
      {!scopedSubjectId && (
        <div data-encounter-draft key={`dyad-${formEpoch}`}>
          <DyadReview
            episode={episode}
            events={list}
            encounterId={encounterId}
            timeZone={timeZone}
            configuration={configuration.data}
            disabled={entryDisabled}
            onSave={save}
          />
        </div>
      )}
      <VisitSignOff
        episodeId={episode.id}
        events={list}
        subjects={episode.subjects}
        timeZone={timeZone}
        disabledReason={
          !offline.unlocked || !offline.cacheReady
            ? 'Unlock the prepared workspace and load its pending observations before signing.'
            : offline.status.pending
              ? 'Sync pending observations before signing this visit.'
              : !navigator.onLine || events.isError || !events.data
                ? 'Connect and reload current observations before signing this visit.'
                : undefined
        }
      />
      <EpisodeAdministration
        key={`${organization?.organization_id}:${user?.id}:${episode.id}`}
        episodeId={episode.id}
        subjects={episode.subjects}
      />
      {!scopedSubjectId && (
        <div data-encounter-draft>
          <PrenatalContextEditor
            episodeId={episode.id}
            subjectId={motherId}
            events={list}
            onSave={save}
            disabled={entryDisabled}
          />
          <DatingReview
            episodeId={episode.id}
            encounterId={encounterId}
            subjectId={motherId}
            edd={pregnancy.edd}
            configuration={configuration.data}
            disabled={entryDisabled || !!offline.status.pending || !navigator.onLine}
          />
          <CareStatusEntry subjectId={motherId} onSave={save} disabled={entryDisabled} />
        </div>
      )}
      <EpisodeTimeline
        key={`${organization?.organization_id}:${user?.id}:${episode.id}`}
        episodeId={episode.id}
        patientId={patientId}
        events={list}
        subjects={episode.subjects}
        edd={pregnancy.edd}
        timeZone={timeZone}
      />
      <ConsentArtifacts episodeId={episode.id} events={list} />
      <PracticeChecklist
        prenatalFlags={flags.filter((flag) => flag.ruleId.startsWith('R-PN-'))}
        events={list}
        edd={pregnancy.edd}
        subjectId={episode.subjects.find((s) => s.subject_type === 'MOTHER')?.id ?? ''}
        onSave={save}
        disabled={entryDisabled}
      />
      <Card>
        <CardHeader>
          <CardTitle>Review and audit history</CardTitle>
          <CardDescription>
            Amendments and strikes append a new entry; the original observation is retained.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EventHistory
            episodeId={episode.id}
            events={list}
            subjects={episode.subjects}
            lateEntryMinutes={configuration.data?.late_entry_minutes}
            editRequest={editRequest}
            customDefinitions={configuration.data?.custom_observations}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/** Reuses the same encrypted save path while keeping a linked baby encounter scoped to that baby. */
export function NewbornFollowup({
  episodeId,
  motherPatientId,
  subjectId,
}: {
  episodeId: string;
  motherPatientId: string;
  subjectId: string;
}) {
  const { organization } = useAuth();
  const pregnancies = usePregnancies(motherPatientId);
  const prepared = useQuery({
    queryKey: ['newborn-course-preparation', organization?.organization_id, episodeId],
    enabled: !!organization,
    queryFn: async () =>
      (
        await clientApi.get<{ episode: ClinicalEpisode; context: OfflineEpisodeContext }>(
          `/api/v1/organizations/${organization!.organization_id}/flowsheets/episodes/${episodeId}/preparation`,
        )
      ).data,
  });
  const pregnancy = pregnancies.data?.data.find((p) => p.id === prepared.data?.episode.pregnancy_id);
  if (prepared.isError || pregnancies.isError)
    return (
      <Alert>
        <AlertDescription>
          Linked follow-up entry could not be prepared. Review access to the maternal episode.
        </AlertDescription>
      </Alert>
    );
  if (!prepared.data || !pregnancy) return <Skeleton className="h-24 w-full" />;
  const first = prepared.data.context.encounters.find(
    (e) => e.allowed_subject_ids?.length === 1 && e.allowed_subject_ids.includes(subjectId),
  );
  if (!first)
    return (
      <p>
        No newborn-only encounter is prepared yet. Book a reviewed newborn follow-up from the linked maternal course.
      </p>
    );
  return (
    <EpisodeView
      key={`${episodeId}:${subjectId}`}
      episode={prepared.data.episode}
      pregnancy={pregnancy}
      patientId={motherPatientId}
      scopedSubjectId={subjectId}
      initialEncounterId={first.id}
    />
  );
}
